/**
 * InquiriesStatsTile — compact dashboard card showing total + this-month +
 * new application inquiries from the public landing page. Auto-hides on 403
 * (caller has no perm) and shows a friendly empty state when total is 0.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Inbox, ArrowRight, Sparkles } from "lucide-react";

export default function InquiriesStatsTile() {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/inquiries/stats");
        if (!cancelled) setData(res.data || {});
      } catch {
        if (!cancelled) setHidden(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (hidden || !data) return null;

  const total = data.total || 0;

  return (
    <Link
      to="/inquiries"
      className="bg-white rounded-2xl border border-[#2563EB]/10 shadow-floating p-5 hover:border-[#2563EB]/40 hover:shadow-floating-hover transition-all group block"
      data-testid="dash-inquiries-tile"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#FEF3C7] to-[#FDE68A] flex items-center justify-center shadow-sm">
            <Inbox className="w-5 h-5 text-[#92400E]" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-foreground tracking-tight">Application Inquiries</h3>
            <p className="text-xs text-slate-500">From your public school landing page</p>
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#2563EB] group-hover:translate-x-0.5 transition-all" />
      </div>

      {total === 0 ? (
        <div className="mt-4 rounded-xl bg-[#F8FAFC] p-3 flex items-start gap-2 text-xs text-slate-600">
          <Sparkles className="w-3.5 h-3.5 text-[#2563EB] mt-0.5 shrink-0" />
          <span>No inquiries yet. Share your <span className="font-semibold text-[#1D4ED8]">/school/{"{slug}"}</span> link on social media to start collecting leads.</span>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 mt-4">
          <Stat label="This month" value={data.this_month || 0} accent="text-[#1D4ED8]" />
          <Stat label="New / unread" value={data.new || 0} accent="text-[#B45309]" />
          <Stat label="Enrolled" value={data.converted || 0} accent="text-[#15803D]" />
        </div>
      )}
    </Link>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-xl bg-[#F8FAFC] p-2.5 text-center">
      <div className={`font-heading text-2xl font-bold ${accent}`}>{value}</div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5 leading-tight">{label}</div>
    </div>
  );
}
